import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faShoppingCart, faShoppingBag, faTruck } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { loadStripe } from "@stripe/stripe-js";
import { Autocomplete } from "@react-google-maps/api";
import PhoneInput from "react-phone-number-input";
import 'react-phone-number-input/style.css';
import "../assets/css/CartTable.css";

// Initialise Stripe lazily so a missing publishable key surfaces as a handled
// checkout error instead of an uncaught rejection at module load.
let stripePromise = null;
const getStripe = () => {
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  if (!publishableKey) return Promise.resolve(null);
  if (!stripePromise) stripePromise = loadStripe(publishableKey);
  return stripePromise;
};

const steps = [
  { icon: faShoppingBag, label: "Cart" },
  { icon: faTruck, label: "Delivery" },
  { icon: faShoppingCart, label: "Pay" }
];

const CartTable = ({ cart, setCart, removeFromCart, campaignId }) => {
  const autocompleteRef = useRef(null);
  const [step, setStep] = useState(0);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    address: "",
    phone: "",
  });
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  // Helpers
  const sizeMultiplier = { Whole: 1, Half: 0.5, Quarter: 0.25, Eighth: 0.125, Sixteenth: 0.0625 };
  const deliveryFee = 15;
  const calculateItemTotal = (item) => item.price * (sizeMultiplier[item.size] || 1) * item.quantity;
  const grandTotal = cart.reduce((acc, item) => acc + calculateItemTotal(item), 0);
  const totalWithDelivery = grandTotal + deliveryFee;

  const validateForm = () => {
    if (!customerInfo.name.trim() || !customerInfo.email.trim() || !customerInfo.address.trim() || !customerInfo.phone.trim()) {
      setFormError("Please fill in all the fields.");
      return false;
    }
    setFormError("");
    return true;
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const stripe = await getStripe();
      if (!stripe) throw new Error("Payments are not available right now. Please try again later.");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/campaign/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cartDetails: cart, customerInfo, campaignId, deliveryFee })
      });
      if (!response.ok) throw new Error("Failed to create payment session. Try again.");
      const { id: sessionId } = await response.json();
      const result = await stripe.redirectToCheckout({ sessionId });
      if (result.error) throw new Error(result.error.message || "Stripe redirect failed.");
    } catch (err) {
      setLoading(false);
      Swal.fire({ title: "Payment Error", text: err.message || "There was a problem starting your payment. Please try again.", icon: "error", confirmButtonText: "OK" });
    }
  };

  const Stepper = () => (
      <div className="stepper">
        {steps.map((s, idx) => (
            <div className={`step ${step === idx ? "active" : ""} ${step > idx ? "completed" : ""}`} key={s.label}>
              <FontAwesomeIcon icon={s.icon} />
              <span>{s.label}</span>
              {idx !== steps.length - 1 && <div className="step-separator" />}
            </div>
        ))}
      </div>
  );

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.formatted_address) return;
    const address = place.formatted_address.toLowerCase();
    if (!address.includes("calgary") || (!address.includes("alberta") && !address.includes("ab"))) {
      Swal.fire({ icon: 'error', title: 'Invalid Address', text: 'We currently only deliver to Calgary, Alberta. Please update your address.' });
      return;
    }
    setCustomerInfo((prev) => ({ ...prev, address: place.formatted_address }));
  };

  const fadeClass = "fade-in";

  // Places autocomplete needs the Maps script; without it the field still works
  // as a plain text input rather than breaking the checkout flow.
  const mapsReady = typeof window !== "undefined" && !!window.google?.maps?.places;

  const addressField = (
      <input
          type="text"
          name="address"
          value={customerInfo.address}
          onChange={e => setCustomerInfo({ ...customerInfo, address: e.target.value })}
          placeholder="Start typing your Calgary address"
          className="form-control"
          required
      />
  );

  const addressInput = mapsReady ? (
      <Autocomplete
          onLoad={auto => (autocompleteRef.current = auto)}
          onPlaceChanged={onPlaceChanged}
          options={{
            componentRestrictions: { country: "ca" },
            types: ["address"],
            bounds: { north: 51.2, south: 50.8, east: -113.8, west: -114.3 },
            strictBounds: true,
          }}
      >
        {addressField}
      </Autocomplete>
  ) : (
      addressField
  );

  return (
      <div className="cart-container">
        {loading && (
            <div className="payment-loader-overlay">
              <div className="payment-loader-spinner"></div>
              <div className="payment-loader-text">Processing your payment...</div>
            </div>
        )}
        <Stepper />

        {step === 0 && (
            <div className={`cart-card ${fadeClass}`}>
              <h2 className="cart-section-title"><FontAwesomeIcon icon={faShoppingBag} /> Cart</h2>
              {cart.length === 0 ? (
                  <div className="empty-cart">
                    <FontAwesomeIcon icon={faShoppingCart} className="empty-cart-icon" />
                    <div>Your cart is empty. Add a product to get started!</div>
                  </div>
              ) : (
                  <>
                    <table className="cart-table">
                      <thead>
                      <tr>
                        <th>Product</th>
                        <th>Size</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                        <th></th>
                      </tr>
                      </thead>
                      <tbody>
                      {cart.map((item, idx) => (
                          <tr key={idx}>
                            <td data-label="Product">{item.productType}</td>
                            <td data-label="Size">{item.size}</td>
                            <td data-label="Qty">{item.quantity}</td>
                            <td data-label="Unit Price">${(item.price * (sizeMultiplier[item.size] || 1)).toLocaleString()}</td>
                            <td data-label="Total">${calculateItemTotal(item).toLocaleString()}</td>
                            <td className="cart-row-action">
                              <button className="remove-btn" onClick={() => removeFromCart(idx)}>
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                            </td>
                          </tr>
                      ))}
                      </tbody>
                    </table>
                    <div className="cart-total-row">
                      <div className="cart-summary-row">
                        <span className="label">Subtotal:</span>
                        <span className="value">${grandTotal.toLocaleString()}</span>
                      </div>
                      <div className="cart-summary-row">
                        <span className="label">Delivery Fee:</span>
                        <span className="value">${deliveryFee.toLocaleString()}</span>
                      </div>
                      <div className="cart-summary-row total">
                        <span className="label">Total:</span>
                        <span className="value">${totalWithDelivery.toLocaleString()}</span>
                      </div>
                    </div>


                    <button className="primary-btn" onClick={() => setStep(1)}>Next: Delivery</button>
                  </>
              )}
            </div>
        )}

        {step === 1 && (
            <div className={`cart-card ${fadeClass}`}>
              <h2 className="cart-section-title"><FontAwesomeIcon icon={faTruck} /> Delivery</h2>
              <form className="modern-form" onSubmit={e => { e.preventDefault(); if (validateForm()) setStep(2); }}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input type="text" name="name" value={customerInfo.name} onChange={e => setCustomerInfo({ ...customerInfo, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={customerInfo.email} onChange={e => setCustomerInfo({ ...customerInfo, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <PhoneInput name="phone" defaultCountry="CA" value={customerInfo.phone} onChange={value => setCustomerInfo({ ...customerInfo, phone: value })} required />
                </div>
                <div className="form-group">
                  <label>Delivery Address</label>
                  {addressInput}
                  <p className="field-hint">We currently deliver within Calgary, Alberta.</p>
                </div>
                {formError && <div className="form-error">{formError}</div>}
                <div className="checkout-actions">
                  <button className="secondary-btn" type="button" onClick={() => setStep(0)}>Back to Cart</button>
                  <button className="primary-btn" type="submit">Next: Pay</button>
                </div>
              </form>
            </div>
        )}

        {step === 2 && (
            <div className={`cart-card ${fadeClass}`}>
              <h2 className="cart-section-title"><FontAwesomeIcon icon={faShoppingCart} /> Pay</h2>
              <div className="summary-box">
                <h4>Review & Confirm</h4>
                <div><b>Name:</b> {customerInfo.name}</div>
                <div><b>Email:</b> {customerInfo.email}</div>
                <div><b>Phone:</b> {customerInfo.phone}</div>
                <div><b>Address:</b> {customerInfo.address}</div>
                <div><b>Subtotal:</b> ${grandTotal.toLocaleString()}</div>
                <div><b>Delivery Fee:</b> ${deliveryFee.toLocaleString()}</div>
                <div><b>Total:</b> <strong>${totalWithDelivery.toLocaleString()}</strong></div>
              </div>
              <div className="checkout-actions">
                <button className="secondary-btn" type="button" onClick={() => setStep(1)}>Back</button>
                <button className="primary-btn" onClick={handleSubmitOrder} disabled={loading}>
                  {loading ? "Processing..." : "Pay Now"}
                </button>
              </div>
            </div>
        )}
      </div>
  );
};

export default CartTable;
